import * as React from 'react';
import {
  Navigate,
  Outlet,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
} from 'react-router-dom';
import { useLocalStorageValue } from '@react-hookz/web';
import { useLazyQuery, useMutation } from '@apollo/client';

import AboutUsPage from '#/pages/static/AboutUsPage';
import AdminCustomerListPage from '#/pages/admin/AdminCustomerListPage';
import AdminLogisticsListPage from '#/pages/admin/AdminLogisticsListPage';
import AdminUserDetailsPage from '#/pages/admin/AdminUserDetailsPage';
import AdminUserListPage from '#/pages/admin/AdminUserListPage';
import ContactUsPage from '#/pages/static/ContactUsPage';
import CustomerSignupPage from '#/pages/CustomerSignupPage';
import FaqPage from '#/pages/static/FaqPage';
import HomePage from '#/pages/static/HomePage';
import LoginPage from '#/pages/LoginPage';
import LogisticsSignupPage from '#/pages/LogisticsSignupPage';
import NotFoundPage from '#/pages/404Page';
import O11y from '#/utils/o11y';
import OrderPage from '#/pages/OrderPage';
import PaymentConfirmPage from '#/pages/PaymentConfirmPage';
import PrivacyPage from '#/pages/static/PrivacyPage';
import RequestForQuotationPage from '#/pages/RequestForQuotationPage';
import RequestPasswordResetPage from '#/pages/RequestPasswordResetPage';
import ResetPasswordPage from '#/pages/ResetPasswordPage';
import TermsOfServicePage from '#/pages/static/TermsOfServicePage';
import { AUTH_TOKEN_KEY, MANAGING_PROFILE_ID_KEY, REFRESH_TOKEN_KEY } from '#/constants/auth';
import { AdminRoute, PrivateRoute, PublicRoute } from '#/constants/page-route';
import { EnforcerContext, UserContext } from '#/hooks';
import { LogoutDocument, MeDocument } from '#/__generated__/graphql/graphql';

const PrivateRouteGuard: React.FC<PrivateRouteGuardProps> = () => {
  const [accessToken] = useLocalStorageValue(AUTH_TOKEN_KEY);
  const [managingProfileId, setManagingProfileId] = useLocalStorageValue(MANAGING_PROFILE_ID_KEY);
  const location = useLocation();
  const [executeQuery, { data, loading }] = useLazyQuery(MeDocument);

  React.useEffect(() => {
    const managingProfile = data?.me.manageCustomers[0] || data?.me.manageLogistics[0];
    if (!managingProfileId && managingProfile) {
      setManagingProfileId(managingProfile.id);
    }
  }, [data, managingProfileId, setManagingProfileId]);
  React.useEffect(() => {
    if (accessToken && !loading) {
      executeQuery();
    }
  }, [accessToken, loading, executeQuery]);

  if (!accessToken) {
    return <Navigate to={PublicRoute.Login} state={{ from: location }} replace />;
  }

  if (loading || !data) {
    return null;
  }

  if (data.me.roles.some((role) => ['superadmin', 'admin'].includes(role.slug))) {
    return <Navigate to={AdminRoute.Home} state={{ from: location }} replace />;
  }

  return (
    <UserContext.Provider value={data.me}>
      <EnforcerContext.Provider value={data?.me.roles.flatMap((r) => r.permissions) || []}>
        <Outlet />
      </EnforcerContext.Provider>
    </UserContext.Provider>
  );
};
export type PrivateRouteGuardProps = React.ComponentProps<typeof Route>;

const AdminRouteGuard: React.FC<AdminRouteGuardProps> = () => {
  const [accessToken] = useLocalStorageValue(AUTH_TOKEN_KEY);
  const location = useLocation();
  const [executeQuery, { data, loading }] = useLazyQuery(MeDocument);

  React.useEffect(() => {
    if (accessToken && !loading) {
      executeQuery();
    }
  }, [accessToken, loading, executeQuery]);

  if (!accessToken) {
    return <Navigate to={PublicRoute.Login} state={{ from: location }} replace />;
  }

  if (loading || !data) {
    return null;
  }

  if (data.me.roles.some((role) => !['superadmin', 'admin'].includes(role.slug))) {
    return <Navigate to={PrivateRoute.Home} state={{ from: location }} replace />;
  }

  return (
    <UserContext.Provider value={data.me}>
      <EnforcerContext.Provider value={data?.me.roles.flatMap((r) => r.permissions) || []}>
        <Outlet />
      </EnforcerContext.Provider>
    </UserContext.Provider>
  );
};
export type AdminRouteGuardProps = React.ComponentProps<typeof Route>;

const PublicRouteGuard: React.FC<PublicRouteGuardProps> = () => {
  const [accessToken] = useLocalStorageValue(AUTH_TOKEN_KEY);
  // TODO: use proper typing
  const { state } = useLocation() as { state?: { from?: string } };

  if (accessToken) {
    return <Navigate to={state?.from || PrivateRoute.Home} />;
  }

  return <Outlet />;
};
export type PublicRouteGuardProps = React.ComponentProps<typeof Route>;

const Logout = () => {
  const [logoutMutation, logoutMutationResult] = useMutation(LogoutDocument);
  const [accessToken, , deleteAccessToken] = useLocalStorageValue(AUTH_TOKEN_KEY);
  const [, , deleteRefreshToken] = useLocalStorageValue(REFRESH_TOKEN_KEY);
  const [, , deleteManagingProfileId] = useLocalStorageValue(MANAGING_PROFILE_ID_KEY);

  React.useEffect(() => {
    if (accessToken && !logoutMutationResult.called) {
      logoutMutation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);
  React.useEffect(() => {
    if (!logoutMutationResult.loading && logoutMutationResult.called) {
      logoutMutationResult.client.resetStore();
      deleteManagingProfileId();
      deleteRefreshToken();
      deleteAccessToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoutMutationResult]);

  if (!accessToken) {
    return <Navigate to={PublicRoute.Login} replace />;
  }

  return null;
};

const Routes = (
  <>
    <Route path={PublicRoute.AboutUs} element={<AboutUsPage />} />
    <Route path={PublicRoute.ContactUs} element={<ContactUsPage />} />
    <Route path={PublicRoute.CustomerSignup} element={<CustomerSignupPage />} />
    <Route path={PublicRoute.Faq} element={<FaqPage />} />
    <Route path={PublicRoute.Home} element={<HomePage />} />
    <Route path={PublicRoute.LogisticsSignup} element={<LogisticsSignupPage />} />
    <Route path={PublicRoute.Logout} element={<Logout />} />
    <Route path={PublicRoute.PrivacyPolicy} element={<PrivacyPage />} />
    <Route path={PublicRoute.TermsOfService} element={<TermsOfServicePage />} />

    <Route element={<PublicRouteGuard />}>
      <Route path={PublicRoute.Login} element={<LoginPage />} />
      <Route path={PublicRoute.RequestPasswordReset} element={<RequestPasswordResetPage />} />
      <Route path={PublicRoute.ResetPassword} element={<ResetPasswordPage />} />
    </Route>

    <Route element={<PrivateRouteGuard />}>
      <Route path={PrivateRoute.Home} element={<Navigate to={PrivateRoute.OrderList} replace />} />

      {/* <Route index element={<DashboardPage />} /> */}
      <Route path={PrivateRoute.OrderList} element={<OrderPage />} />
      <Route path={PrivateRoute.PaymentConfirm} element={<PaymentConfirmPage />} />
      <Route path={PrivateRoute.RequestNew} element={<RequestForQuotationPage />} />
    </Route>

    <Route path={AdminRoute.Home} element={<AdminRouteGuard />}>
      <Route path={AdminRoute.Home} element={<Navigate to={AdminRoute.UserList} replace />} />

      <Route path={AdminRoute.CustomerList} element={<AdminCustomerListPage />} />
      <Route path={AdminRoute.LogisticsList} element={<AdminLogisticsListPage />} />
      <Route path={AdminRoute.OrderList} element={<OrderPage />} />
      <Route path={AdminRoute.UserDetails} element={<AdminUserDetailsPage />} />
      <Route path={AdminRoute.UserList} element={<AdminUserListPage />} />
      <Route path={AdminRoute.UserNew} element={<AdminUserDetailsPage />} />
    </Route>

    <Route path="*" element={<NotFoundPage />} />
  </>
);

const router = O11y.wrapCreateBrowserRouter(createBrowserRouter)(createRoutesFromElements(Routes));

export default router;
